import { LogEvents, logger } from 'lib/common/components/LoggerController';

import CONTACT_STATES from 'lib/common/constants/contactStates';

import { getAllActiveConnections, isMultiPartyConferenceEnabled } from 'lib/common/utils/conferenceConnections';
import connectAction from 'lib/common/utils/connectAction';
import connectGetter from 'lib/common/utils/connectGetter';
import { getTask, getTaskContact } from 'lib/common/utils/getTask';
import redactTask from 'lib/common/utils/redactTask';

import { useAgentContext } from '../../AgentContext';
import { TaskAction } from '../constants/taskAction';
import { TAnyTask } from '../types/ContactContext';
import destroyTask from '../utils/destroyTask';
import doesContactExist from '../utils/doesContactExist';
import endInitialConnectionHelper from '../utils/endInitialConnectionHelper';
import hangupAgentHelper from '../utils/hangupAgentHelper';
import useClearContact from './useClearContact';
import useMoveTaskToACW from './useMoveTaskToACW';

export default function useTaskActions({ tasks, handleContactChange, setSelectedTaskId, setTask }) {
  const { agent } = useAgentContext();

  const moveTaskToACW = useMoveTaskToACW({ handleContactChange });
  const clearContact = useClearContact({ handleContactChange });

  const pauseTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      logger.error(LogEvents.TASK.PAUSE.FAIL, { taskId, error: 'Contact not found' });

      return Promise.reject();
    }

    try {
      await connectAction('pause', contact);

      handleContactChange({ taskId, status: CONTACT_STATES.PAUSED });

      logger.info(LogEvents.TASK.PAUSE.SUCCESS, { task: redactTask(getTask(tasks, taskId)) });
    } catch (error) {
      logger.error(LogEvents.TASK.PAUSE.FAIL, { taskId, error });

      throw error;
    }
  };

  const resumeTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      logger.error(LogEvents.TASK.RESUME.FAIL, { taskId, error: 'Contact not found' });

      return Promise.reject();
    }

    try {
      await connectAction('resume', contact);

      handleContactChange({ taskId, status: CONTACT_STATES.CONNECTED });

      logger.info(LogEvents.TASK.RESUME.SUCCESS, { task: redactTask(getTask(tasks, taskId)) });
    } catch (error) {
      logger.error(LogEvents.TASK.RESUME.FAIL, { taskId, error });

      throw error;
    }
  };

  const rejectTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!doesContactExist(agent, contact?.contactId)) {
      destroyTask({ contact, handleContactChange });

      return;
    }

    await connectAction('reject', contact);

    handleContactChange({ taskId, status: CONTACT_STATES.REJECTED });
  };

  const acceptTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!doesContactExist(agent, contact?.contactId)) {
      destroyTask({ contact, handleContactChange });

      return;
    }

    setSelectedTaskId(taskId);

    await connectAction('accept', contact);
  };

  const endInitialConnection = async (taskId: string) =>
    endInitialConnectionHelper(getTaskContact(tasks, taskId), handleContactChange);

  const hangupAgent = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    await hangupAgentHelper({ agent, contactId: contact?.contactId });

    return moveTaskToACW(contact);
  };

  const removeTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      logger.error(LogEvents.TASK.CLOSED.FAIL, { taskId });

      return handleContactChange({ taskId, isDestroy: true });
    }

    await clearContact(contact);

    logger.info(LogEvents.TASK.CLOSED.SUCCESS, { task: redactTask(getTask(tasks, taskId)) });
  };

  const removeDraftTask = (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return;
    }

    destroyTask({ contact, handleContactChange });
  };

  const onJoinConference = async ({ taskId }: { taskId: string }) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    try {
      await connectAction('conferenceConnections', contact);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const onSwapConferenceCall = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    const activeConnections = getAllActiveConnections(contact);

    if (isMultiPartyConferenceEnabled(contact)) {
      return activeConnections.forEach((connection) =>
        connectGetter(connection, 'getState')?.type === connect.ConnectionStateType.CONNECTED
          ? connectAction('hold', connection)
          : connectAction('resume', connection)
      );
    }

    await connectAction('toggleActiveConnections', contact);
  };

  const addDraftTask = (newTask: TAnyTask) => {
    setTask({ task: newTask, action: TaskAction.CREATE });
  };

  const monitorTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    await connectAction('silentMonitor', contact);
  };

  const bargeInTask = async (taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    await connectAction('bargeIn', contact);
  };

  return {
    rejectTask,
    acceptTask,
    endInitialConnection,
    removeTask,
    hangupAgent,
    onJoinConference,
    onSwapConferenceCall,
    addDraftTask,
    removeDraftTask,
    monitorTask,
    bargeInTask,
    pauseTask,
    resumeTask,
  };
}
